import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, Theme } from '@mui/material/styles';
import {
  Collapse,
  Divider,
  Drawer,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { signOut } from '../../redux/slices/authSlice';
import useRoutes, { Route } from '../../containers/Router/useRoutes';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins, setCurrentCity } from '../../redux/slices/adminsSlice';
import Logo from '../Logo';
import { RootState } from '../../redux/store';
import usePermissions from '../../hooks/usePermissions';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useResponsiveLayout } from '../../contexts/ResponsiveLayoutContext';
import { adminsApi } from '../../redux/api/adminsApi';
import { batteriesApi } from '../../redux/api/batteriesApi';
import { scootersApi } from '../../redux/api/scootersApi';
import { noticesApi } from '../../redux/api/noticesApi';
import { workshopTasksApi } from '../../redux/api/workshopTasksApi';
import { driverTasksApi } from '../../redux/api/driverTasksApi';
import { shiftsApi } from '../../redux/api/shiftsApi';
import { sparePartsApi } from '../../redux/api/sparePartsApi';

const PREFIX = 'Sidebar';

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  bottomSection: `${PREFIX}-bottomSection`,
  exitButton: `${PREFIX}-exitButton`,
  list: `${PREFIX}-list`,
  link: `${PREFIX}-link`,
  activeLink: `${PREFIX}-activeLink`,
  nested: `${PREFIX}-nested`
};

// Стилі меню лежать окремо, бо їх ділять три drawer'и: десктопний
// persistent, мобільний Drawer (iOS) і SwipeableDrawer (решта). Раніше вони
// були вбудовані у styled(Drawer), тож на телефоні класи проставлялись, а
// правил до них не існувало — меню виглядало голим списком без блоків.
const sidebarStyles = (theme: Theme) => ({
  [`&.${classes.drawer}`]: {
    flexShrink: 0
  },

  [`& .${classes.drawerPaper}`]: {
    width: SIDEBAR_WIDTH,
    backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1A1A1A',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundImage: theme.palette.mode === 'light' 
      ? 'linear-gradient(180deg, rgba(80, 115, 248, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
      : 'linear-gradient(180deg, rgba(80, 115, 248, 0.02) 0%, rgba(26, 26, 26, 1) 100%)'
  },

  [`& .${classes.bottomSection}`]: {
    padding: '0 16px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px'
  },

  [`& .${classes.exitButton}`]: {
    marginTop: 8,
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: 'rgba(239, 83, 80, 0.08)'
    }
  },

  [`& .${classes.list}`]: {
    padding: '8px 12px',
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.mode === 'light'
        ? 'rgba(80, 115, 248, 0.3)'
        : 'rgba(80, 115, 248, 0.2)',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.mode === 'light'
          ? 'rgba(80, 115, 248, 0.4)'
          : 'rgba(80, 115, 248, 0.3)'
      }
    }
  },

  [`& .${classes.link}`]: {
    padding: '10px 16px',
    borderRadius: 12,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    userSelect: 'none',
    marginBottom: 4,
    transition: 'all 0.2s ease',
    '& + &': {
      marginTop: 0
    },
    '& svg': {
      color: theme.palette.text.secondary,
      transition: 'color 0.2s ease'
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(80, 115, 248, 0.08)'
        : 'rgba(80, 115, 248, 0.08)',
      '& svg': {
        color: theme.palette.primary.main
      }
    }
  },

  [`& .${classes.activeLink}`]: {
    backgroundColor: theme.palette.mode === 'light'
      ? 'rgba(80, 115, 248, 0.12)'
      : 'rgba(80, 115, 248, 0.15)',
    color: theme.palette.primary.main,
    fontWeight: 600,
    boxShadow: theme.palette.mode === 'light' 
      ? '0 0 0 1px rgba(80, 115, 248, 0.3)'
      : '0 0 0 1px rgba(80, 115, 248, 0.3)',
    '& svg': {
      color: theme.palette.primary.main
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(80, 115, 248, 0.18)'
        : 'rgba(80, 115, 248, 0.2)'
    }
  },

  [`& .${classes.nested}`]: {
    paddingLeft: theme.spacing(6),
    fontSize: '0.9rem'
  }
});

const StyledDrawer = styled(Drawer)(({ theme }) => sidebarStyles(theme));

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) =>
  sidebarStyles(theme)
);

export const SIDEBAR_WIDTH = 300;

interface SidebarItemProps {
  link: Route;
  nested?: boolean;
  onNavigate?: () => void;
}

// Так само, як матчить NavLink: по сегментах, а не по рядку. Інакше /zones
// вважався б активним і на /zones-management.
const isPathActive = (pathname: string, path?: string) =>
  Boolean(path) && (pathname === path || pathname.startsWith(`${path}/`));

const SidebarItem = (props: SidebarItemProps) => {
  const { link, onNavigate } = props;
  const checkPermission = usePermissions();
  const { pathname } = useLocation();

  const isHavePermission = useMemo(() => {
    if (!link?.code) {
      return true;
    }
    return checkPermission(link.code);
  }, [link?.code, checkPermission]);

  const hasActiveChild = useMemo(
    () => Boolean(link.nested?.some(item => isPathActive(pathname, item.path))),
    [link.nested, pathname]
  );

  // Розділ із відкритою сторінкою розгорнутий одразу: раніше він стартував
  // закритим, а Collapse має unmountOnExit — активний пункт навіть не
  // потрапляв у DOM, тож підсвітки не було видно взагалі.
  const [open, setOpen] = useState(hasActiveChild);

  // Тільки розкриваємо: якщо користувач сам згорнув розділ, з якого перейшов,
  // ми не змикаємо назад те, що він відкрив руками.
  useEffect(() => {
    if (hasActiveChild) {
      setOpen(true);
    }
  }, [hasActiveChild]);

  const toggleCollapse = useCallback(
    () => setOpen(prevState => !prevState),
    []
  );

  const Item = useCallback(
    ({ item, nested, onClick, active }) => {
      const hasAccess = item?.code ? checkPermission(item.code) : true;

      if (!hasAccess) return null;

      const handleClick = () => {
        if (item.nested) {
          onClick?.();
        } else {
          onNavigate?.();
        }
      };

      return (
        <ListItem
          activeClassName={classes.activeLink}
          className={classNames(
            classes.link,
            nested && classes.nested,
            // Заголовок розділу підсвічуємо лише коли він згорнутий:
            // інакше світилися б і розділ, і пункт усередині нього.
            active && classes.activeLink
          )}
          component={item.nested ? 'div' : NavLink}
          key={item.path}
          to={item.path}
          onClick={handleClick}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
          {item.nested && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItem>
      );
    },
    [open, checkPermission, onNavigate]
  );

  if (!isHavePermission) return null;

  return (
    <>
      <Item
        active={hasActiveChild && !open}
        item={link}
        onClick={toggleCollapse}
      />
      {link.nested && (
        <Collapse unmountOnExit in={open} timeout='auto'>
          <List disablePadding className={classes.list} component='div'>
            {link.nested.map((nestedLink, index) => (
              <Item key={index} nested item={nestedLink} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Sidebar = () => {
  const dispatch = useDispatch();
  const links = useRoutes();
  const { mode, toggleTheme } = useThemeMode();
  const { isMobile, isSidebarOpen, closeSidebar, toggleSidebar } = useResponsiveLayout();

  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector((state: RootState) => state.auth);
  const sidebarVisible = useSelector(
    (state: RootState) => state.app.sidebarVisible
  );

  const [cityMenuAnchor, setCityMenuAnchor] = useState<null | HTMLElement>(null);

  const handleNavigate = useCallback(() => {
    if (isMobile) {
      closeSidebar();
    }
  }, [isMobile, closeSidebar]);

  // Filter cities based on user type
  const availableCities = useMemo(() => {
    // SuperAdmin and all_read_only sees all cities
    const isAllReadOnly = auth.info?.type === 'all_read_only' || adminInfo.info?.type === 'all_read_only' || adminInfo.info?.login === 'all_read_only';
    if (auth.info?.type === 'superAdmin' || isAllReadOnly) {
      return cities.items;
    }
    // Non-superAdmin sees only their assigned cities
    if (adminInfo.info?.cities) {
      const adminCityIds = new Set(adminInfo.info.cities.map(c => c._id));
      return cities.items.filter(city => adminCityIds.has(city._id));
    }
    return [];
  }, [auth.info?.type, adminInfo.info?.type, adminInfo.info?.login, adminInfo.info?.cities, cities.items]);

  const cityName = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city.cityName;
    }
  }, [adminInfo.currentCity, cities.items]);

  const handleCityMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCityMenuAnchor(event.currentTarget);
  };

  const handleCityMenuClose = () => {
    setCityMenuAnchor(null);
  };

  const handleCitySelect = (cityId: string) => {
    if (cityId !== adminInfo.currentCity) {
      dispatch(setCurrentCity(cityId));
      handleCityMenuClose();

      dispatch(adminsApi.util.resetApiState());
      dispatch(batteriesApi.util.resetApiState());
      dispatch(scootersApi.util.resetApiState());
      dispatch(noticesApi.util.resetApiState());
      dispatch(workshopTasksApi.util.resetApiState());
      dispatch(driverTasksApi.util.resetApiState());
      dispatch(shiftsApi.util.resetApiState());
      dispatch(sparePartsApi.util.resetApiState());

      if (isMobile) {
        closeSidebar();
      }
    } else {
      handleCityMenuClose();
    }
  };

  if (!sidebarVisible) {
    return null;
  }

  const drawerContent = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1 }}>
        <Logo alignSelf='center' />
      </Box>
      {adminInfo.login && (
        <Typography
          sx={{
            textAlign: 'center',
            pt: 1,
            fontSize: '0.8rem',
            color: 'text.secondary'
          }}
        >
          {adminInfo.login}
        </Typography>
      )}
      <Box sx={{
        textAlign: 'center', 
        pb: 2,
        pt: 1,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '& p': {
          fontSize: '0.9rem',
          fontWeight: 500,
          color: 'text.primary',
          transition: 'color 0.2s ease'
        },
        '&:hover': {
          '& p': {
            color: 'primary.main'
          }
        }
      }}
      onClick={handleCityMenuOpen}
      >
        <Typography>{cityName || '-'}</Typography>
      </Box>
      <Menu
        anchorEl={cityMenuAnchor}
        open={Boolean(cityMenuAnchor)}
        onClose={handleCityMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            maxHeight: 300,
            borderRadius: 2,
            boxShadow: (theme) => theme.palette.mode === 'light' 
              ? '0 4px 20px rgba(0, 0, 0, 0.1)'
              : '0 4px 20px rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        {availableCities.map((city) => (
          <MenuItem 
            key={city._id}
            selected={city._id === adminInfo.currentCity}
            onClick={() => handleCitySelect(city._id)}
            sx={{
              fontWeight: city._id === adminInfo.currentCity ? 600 : 400,
              color: city._id === adminInfo.currentCity 
                ? (theme) => theme.palette.mode === 'light' ? '#3D5BD4' : 'primary.main'
                : 'text.primary',
              '&.Mui-selected': {
                backgroundColor: (theme) => theme.palette.mode === 'light'
                  ? 'rgba(255, 235, 59, 0.12)'
                  : 'rgba(255, 235, 59, 0.15)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'light'
                    ? 'rgba(255, 235, 59, 0.18)'
                    : 'rgba(255, 235, 59, 0.2)'
                }
              }
            }}
          >
            {city.cityName}
          </MenuItem>
        ))}
      </Menu>
      <List className={classes.list}>
        {links.map((link, index) => (
          <SidebarItem key={index} link={link} onNavigate={handleNavigate} />
        ))}
      </List>
      <div>
        <Divider style={{ marginBottom: 8 }} />
        <div className={classes.bottomSection}>
          <ListItem
            button
            className={`${classes.link} ${classes.exitButton}`}
            onClick={() => dispatch(signOut())}
          >
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary='Вихід' />
          </ListItem>
          <Tooltip title={mode === 'light' ? 'Темна тема' : 'Світла тема'}>
            <IconButton 
              onClick={toggleTheme} 
              sx={{ 
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(255, 235, 59, 0.08)'
                }
              }}
            >
              {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </>
  );

  // Detect iOS (SwipeableDrawer has issues with touch events on iOS)
  // Simplified approach: treat any device with maxTouchPoints > 1 AND iOS indicators as iOS
  const detectIOS = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    
    // Direct iOS detection (iPhone, iPad, iPod)
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (platform === 'iPad') return true; // iPadOS in mobile mode
    
    // Modern iPadOS detection (13+): MacIntel + touch support
    // This includes Safari, Chrome, Firefox on iPadOS
    if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
    
    // Check userAgentData API (newer browsers)
    if ('userAgentData' in navigator) {
      const uaData = (navigator as any).userAgentData;
      if (uaData?.platform === 'iOS' || uaData?.platform === 'iPadOS') return true;
    }
    
    return false;
  };
  
  const isIOS = detectIOS();

  if (isMobile) {
    // Use regular Drawer on iOS to avoid SwipeArea blocking touch events
    if (isIOS) {
      return (
        <StyledDrawer
          anchor='left'
          open={isSidebarOpen}
          onClose={closeSidebar}
          classes={{
            paper: classes.drawerPaper
          }}
          sx={{
            '& .MuiDrawer-paper': {
              // 300px на екрані 360px не лишають місця, щоб закрити меню
              // тапом повз нього, тому обмежуємо часткою ширини.
              width: `min(${SIDEBAR_WIDTH}px, 85vw)`
            }
          }}
        >
          {drawerContent}
        </StyledDrawer>
      );
    }

    // Use SwipeableDrawer on other mobile devices
    return (
      <StyledSwipeableDrawer
        anchor='left'
        open={isSidebarOpen}
        onClose={closeSidebar}
        onOpen={toggleSidebar}
        disableSwipeToOpen={true}
        classes={{
          paper: classes.drawerPaper
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: `min(${SIDEBAR_WIDTH}px, 85vw)`
          }
        }}
      >
        {drawerContent}
      </StyledSwipeableDrawer>
    );
  }

  return (
    <StyledDrawer
      anchor='left'
      open={isSidebarOpen}
      classes={{
        paper: classes.drawerPaper
      }}
      className={classes.drawer}
      variant='persistent'
      sx={{
        width: isSidebarOpen ? SIDEBAR_WIDTH : 0,
        flexShrink: 0
      }}
    >
      {drawerContent}
    </StyledDrawer>
  );
};

export default Sidebar;
